// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import SectionWrapper from "@adl/App/Components/Common/SectionWrapper";
import XngCarousel, { SwiperStyle } from "@adl/App/Components/XngCarousel/XngCarousel";
import { color } from "@adl/XDL/Utilities";
import { xScreen } from "@adl/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrands from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrands";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import FlexItem, { FlexItemProps } from "@insite/content-library/Widgets/Brand/FlexItem";
import FlexWrapContainer, { FlexWrapContainerProps } from "@insite/content-library/Widgets/Brand/FlexWrapContainer";
import Clickable, { ClickableLinkProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    title = "title",
    brandsToDisplay = "brandsToDisplay",
    numberOfBrandsToDisplay = "numberOfBrandsToDisplay",
    brandsSelected = "brandsSelected",
    maximumNumberOfBrandsPerRow = "maximumNumberOfBrandsPerRow",
    maximumBrandImageHeight = "maximumBrandImageHeight",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.brandsToDisplay]: "random" | "select-brands";
        [fields.numberOfBrandsToDisplay]: string;
        [fields.brandsSelected]: string[];
        [fields.maximumNumberOfBrandsPerRow]: string;
        [fields.maximumBrandImageHeight]: number;
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const brandGalleryParameter = {
        brandIds: props.fields.brandsSelected,
        randomize: props.fields.brandsToDisplay === "random",
        pageSize: parseInt(props.fields.numberOfBrandsToDisplay || "0", 10),
        select: "id,name,detailPagePath,logoSmallImagePath,logoAltText",
    };
    const brandsState = getBrandsDataView(state, brandGalleryParameter);
    const shouldLoadBrandGallery = !brandsState.isLoading && !brandsState.value;

    return {
        brandsState,
        shouldLoadBrandGallery,
        brandGalleryParameter,
        // XNG-Changes
        brandsPageLink: getPageLinkByPageType(state, "BrandsPage"),
        isBrandPage: getCurrentPage(state).type === "BrandsPage",
    };
};

const mapDispatchToProps = {
    loadBrandGallery: loadBrands,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface BrandListStyles {
    container?: InjectableCss;
    imageContainer?: FlexWrapContainerProps;
    imageItem?: FlexItemProps;
    link?: ClickableLinkProps;
    image?: LazyImageProps;
    skipToContent?: SkipNavStyles;
    // XNG-Changes
    brandsContainer?: GridContainerProps;
    brandGridItem?: GridItemProps;
    swiperStyles?: SwiperStyle;
}

export const listStyles: BrandListStyles = {
    container: {
        css: css`
            margin-bottom: 52px;
        `,
    },
    imageItem: {
        css: css`
            padding: 5px;
            max-width: 158px;
            min-width: 158px;
            margin: auto;
            height: 100%;
            display: flex;
            justify-content: center;

            ${xScreen("lte", "md")} {
                margin-top: 40px;
                margin-bottom: 40px;
            }
        `,
    },
    image: {
        css: css`
            transition: all ${getProp("theme.transition.duration.regular", "300")}ms ease-in-out;
            &:not(:hover) {
                filter: grayscale(100%);
            }
        `,
    },
    brandsContainer: {
        gap: 20,
    },
    brandGridItem: {
        width: [6, 6, 2, 2, 2],
        css: css`
            justify-content: center;
            align-items: center;
        `,
    },
    link: {
        css: css`
            width: 158px;
            height: 116px;
            &:focus {
                outline: none;
            }
        `,
    },
};

const styles = listStyles;

class BrandGallery extends React.Component<Props> {
    UNSAFE_componentWillMount() {
        const { loadBrandGallery, shouldLoadBrandGallery, brandGalleryParameter } = this.props;
        if (shouldLoadBrandGallery) {
            loadBrandGallery(brandGalleryParameter);
        }
    }

    render() {
        const {
            brandsState: { isLoading, value: brandList },
            fields,
            isBrandPage,
            brandsPageLink,
        } = this.props;

        // XNG-Changes
        if (isLoading || !brandList || !brandList.some(brand => brand.logoSmallImagePath)) {
            return null;
        }

        const desktop = parseInt(fields.maximumNumberOfBrandsPerRow, 10);
        const tablet = Math.ceil(desktop / 2);
        const mobile = Math.ceil(tablet / 2);

        const imgProps = {
            style: {
                width: "auto",
                maxWidth: "100%",
                maxHeight: `${fields.maximumBrandImageHeight}px`,
            },
        };

        const focusFunction = () => {
            const elementToFocus = document.getElementById("collapseButton");
            if (elementToFocus) {
                elementToFocus.focus();
            } else {
                document.getElementById("expandButton")?.focus();
            }
        };
        return (
            <SectionWrapper title={this.props.fields.title} data-test-selector="brandGallery">
                <VisuallyHidden as="h1">{translate("Explore available brands")}</VisuallyHidden>
                <StyledWrapper {...styles.imageContainer} data-test-selector="slides">
                    {isBrandPage ? (
                        <GridContainer {...styles.brandsContainer}>
                            {brandList.map(brand => (
                                <GridItem key={brand.id} {...styles.brandGridItem}>
                                    {brand === brandList[0] && (
                                        <SkipNav
                                            extendedStyles={styles.skipToContent}
                                            text={translate("Skip to full brand list")}
                                            focusFunction={focusFunction}
                                        />
                                    )}
                                    <Clickable href={brand.detailPagePath} {...styles.link}>
                                        <LazyImage
                                            imgProps={imgProps}
                                            src={brand.logoSmallImagePath}
                                            altText={`${translate("Go to brand page")} ${
                                                brand.logoAltText || brand.name
                                            }`}
                                            {...{
                                                transformations: { width: 158 },
                                            }}
                                        />
                                    </Clickable>
                                </GridItem>
                            ))}
                        </GridContainer>
                    ) : (
                        <XngCarousel
                            navigation={true}
                            xSlidesPerView={true}
                            spaceBetween={50}
                            extendedStyles={styles.swiperStyles}
                        >
                            {brandList.map(brand => (
                                <StyledWrapper key={brand.id} {...styles.imageItem}>
                                    {brand === brandList[0] && (
                                        <SkipNav
                                            extendedStyles={styles.skipToContent}
                                            text={translate("Skip to full brand list")}
                                            focusFunction={focusFunction}
                                        />
                                    )}
                                    <Clickable href={brand.detailPagePath} {...styles.link}>
                                        <LazyImage
                                            imgProps={imgProps}
                                            src={brand.logoSmallImagePath}
                                            altText={`${translate("Go to brand page")} ${
                                                brand.logoAltText || brand.name
                                            }`}
                                        />
                                    </Clickable>
                                </StyledWrapper>
                            ))}
                        </XngCarousel>
                    )}
                </StyledWrapper>
            </SectionWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BrandGallery),
    definition: {
        group: "Brands",
        icon: "image",
        displayName: "Brand Gallery",
        allowedContexts: ["BrandsPage", "HomePage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.brandsToDisplay,
                displayName: "Brands to display",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Random",
                        value: "random",
                    },
                    {
                        displayName: "Select brands",
                        value: "select-brands",
                    },
                ],
                defaultValue: "random",
                horizontal: true,
                fieldType: "Contextual",
                tooltip:
                    "Brand logos can be picked at random on page load, or you can select specific brands to display.",
            },
            {
                name: fields.numberOfBrandsToDisplay,
                displayName: "Number of brands to display",
                editorTemplate: "DropDownField",
                defaultValue: "12",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                    { displayName: "9", value: "9" },
                    { displayName: "10", value: "10" },
                    { displayName: "11", value: "11" },
                    { displayName: "12", value: "12" },
                    { displayName: "13", value: "13" },
                    { displayName: "14", value: "14" },
                    { displayName: "15", value: "15" },
                    { displayName: "16", value: "16" },
                    { displayName: "17", value: "17" },
                    { displayName: "18", value: "18" },
                ],
                fieldType: "Contextual",
                isVisible: item => item.fields.brandsToDisplay === "random",
            },
            {
                name: fields.brandsSelected,
                displayName: "Select brands",
                editorTemplate: "SelectBrandsField",
                isVisible: item => item.fields.brandsToDisplay === "select-brands",
                defaultValue: [],
                fieldType: "Contextual",
            },
            {
                name: fields.maximumNumberOfBrandsPerRow,
                displayName: "Maximum brands per row",
                editorTemplate: "DropDownField",
                defaultValue: "6",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                ],
                fieldType: "Contextual",
                tooltip:
                    "Up to 6 brands can display per row. This number should be adjusted based on the number of brands you choose to display. For example, if you display 10 brands you likely want 5 to display per row. Logos will also stack responsively for smaller screen sizes.",
                tooltipPosition: "left",
            },
            {
                name: fields.maximumBrandImageHeight,
                displayName: "Maximum brand image height",
                editorTemplate: "IntegerField",
                defaultValue: 100,
                max: 138,
                fieldType: "Contextual",
                tooltip:
                    "By default, images will scale to meet the width of the screen for the number of brands to display per row. The maximum height value can be used to scale brand logos to be smaller. Default value is 100 pixels.",
            },
        ],
    },
};

export default widgetModule;
